import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import enirmitVideo from '../../../images/Enirmit.mp4';
import styles from './Enirmit.module.css';

const ENirmitProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const responsibilities = [
    { text: 'Idea Generation and Conceptualization', icon: '💡' },
    { text: 'User Experience (UX) and Interface (UI) Design', icon: '🎯' },
    { text: 'Custom Features Development', icon: '⚙️' },
    { text: 'Optimization', icon: '🚀' },
    { text: 'Continuous Innovation', icon: '💎' },
    { text: 'Backend Documentation', icon: '📚' },
    { text: 'Multiple User Creation', icon: '👥' },
    { text: 'Responsibilities Adding', icon: '✅' }
  ];

  const tools = [
    { name: 'WordPress', icon: '🔧', description: 'CMS Platform' },
    { name: 'Figma', icon: '🎨', description: 'Design Tool' },
    { name: 'Photoshop', icon: '🖼️', description: 'Image Editor' },
    { name: 'Shotcut', icon: '🎬', description: 'Video Editor' },
    { name: 'PowerPoint', icon: '📊', description: 'Presentations' },
    { name: 'Illustrator', icon: '✨', description: 'Vector Graphics' },
    { name: 'CorelDraw', icon: '🎭', description: 'Graphic Design' }
  ];

  const skills = [
    'CSS', 'Figma', 'HTML5', 'Svelte', 'Flutter', 
    'CorelDraw', 'WordPress', 'JavaScript', 'Tailwind CSS', 'Adobe Animate'
  ];

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
      {/* Animated Background */}
      <div className={styles.backgroundGradient}>
        <div className={styles.gradientOrb1}></div>
        <div className={styles.gradientOrb2}></div>
        <div className={styles.gradientOrb3}></div>
      </div>

      <div className={styles.contentWrapper}>
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <div className={styles.heroLeft}>
              <div className={`${styles.projectBadge} ${styles.closedProject}`}>
                <span className={styles.badgeDot}></span>
                <span>Closed Project</span>
              </div>
              
              <h1 className={styles.projectTitle}>ERP System for Public Works</h1>
              <p className={styles.projectSubtitle}>Powering Digital in Public Works</p>
              
              <div className={styles.projectMeta}>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Work</span>
                  <span className={styles.metaValue}>Website Designing</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Year</span>
                  <span className={styles.metaValue}>2021</span>
                </div>
                <div className={styles.metaItem}>
                  <span className={styles.metaLabel}>Duration</span>
                  <span className={styles.metaValue}>July 2021</span>
                </div>
              </div>
              
              <p className={styles.heroDescription}>
                Integrated IT system for CPWD operations, enhancing stakeholder collaboration, reducing costs, improving quality, and ensuring safety.
              </p>
              
              
            </div>
            
            <div className={styles.heroRight}>
              <div className={styles.heroVisual}>
                <video 
                  className={styles.heroVideo}
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                >
                  <source src={enirmitVideo} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Introduction</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.contentCard}>
            <p className={styles.contentText}>
              An ERP System for Public Works designed to unify and streamline construction operations by integrating real-time data across projects, resources, and equipment. It enables seamless collaboration between stakeholders—including officials and contractors—helping reduce delays, optimize costs, and improve overall efficiency. Built on decades of domain expertise, the system supports accurate design, estimation, and standards management through modern digital tools. As a strategic e-governance solution, it provides real-time monitoring, transparency, and centralized control over public infrastructure projects, contributing to efficient and scalable development.
            </p>
          </div>
        </section>

        {/* My Role Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>My Role</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.contentCard}>
            <p className={styles.contentText}>
              My role is pivotal in the successful implementation and maintenance of the e-Nirmit ERP application. Your responsibilities span a wide array of tasks, ensuring seamless integration of technology to achieve the organization's vision and mission.
            </p>
          </div>
        </section>

        {/* Responsibilities Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Responsibilities</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.responsibilitiesGrid}>
            {responsibilities.map((responsibility, index) => (
              <div 
                key={index}
                className={styles.responsibilityCard}
                onMouseEnter={() => setHoveredResponsibility(index)}
                onMouseLeave={() => setHoveredResponsibility(null)}
              >
                <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                <div className={styles.cardIcon}>{responsibility.icon}</div>
                <div className={styles.cardText}>{responsibility.text}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Tools Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Tools Used</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.toolsGrid}>
            {tools.map((tool, index) => (
              <div 
                key={index}
                className={styles.toolCard}
                onMouseEnter={() => setHoveredTool(index)}
                onMouseLeave={() => setHoveredTool(null)}
              >
                <div className={styles.toolIcon}>{tool.icon}</div>
                <div className={styles.toolName}>{tool.name}</div>
                <div className={styles.toolDescription}>{tool.description}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Skills Section */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
            <div className={styles.titleUnderline}></div>
          </div>
          <div className={styles.skillsContainer}>
            {skills.map((skill, index) => (
              <div 
                key={index}
                className={styles.skillTag}
              >
                <span className={styles.skillHash}>#</span>
                {skill}
              </div>
            ))}
          </div>
        </section>
      </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer  />
      </div>
    </>
  );
};

export default ENirmitProjectShowcase;